/**
 * Report Generator — TypeScript port of html_generator.py
 *
 * Generates structured report data (ReportData) from pre-processed Athena
 * query results. Produces four tables: Business Metrics, Metric Changes,
 * Revenue Bridge, and Growth Bridge.
 *
 * This module ports the DATA LOGIC only — no HTML generation. React
 * components consume the structured TableData objects for rendering.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ReportData {
  metricsTable: TableData;
  changesTable: TableData;
  bridgeTable: TableData;
  growthTable: TableData;
  kpiSummary: KPISummary[];
  /** Scale for metrics/changes tables (revenue magnitude). */
  scale: number;
  /** Scale for bridge table (revenue change magnitude). */
  bridgeScale?: number;
}

export interface KPISummary {
  label: string;
  value: string;
  change: string;
  positive: boolean;
}

export interface TableData {
  headers: TableHeader[][];
  rows: TableRow[];
}

export interface TableHeader {
  label: string;
  colspan?: number;
  rowspan?: number;
}

export interface TableRow {
  label: string;
  cells: TableCell[];
}

export interface TableCell {
  value: string;
  cssClass: string;
  rawValue: number | null;
}

/** A single dimension entry: [columnPrefix, displayName, 'show'|'hide'] */
type DimEntry = [string, string, "show" | "hide"];

/** Dimension order grouped by page type */
type DimOrder = Record<string, DimEntry[]>;

/** Metric definition: [metricKey, displayLabel] */
type MetricDef = [string, string];

/** Placement hierarchy for bridge rollups */
interface PlacementHierarchy {
  Search: { p1: string[]; p2: string[] };
  Detail: string[];
  Homepage?: string[];
}

/** Row from the pre-processed data (key→string value map) */
export type DataRow = Record<string, string>;

/** Indexed data: composite key → DataRow */
type DataIndex = Record<string, DataRow>;

/** Intermediate metrics dictionary for cross-table calculations */
type MetricsDict = Record<string, string | number | null>;

/** Bridge dictionaries: metric → group → dim → value */
type BridgeDict = Record<
  string,
  Record<string, Record<string, number | string>>
>;

// ---------------------------------------------------------------------------
// Formatting Utilities
// ---------------------------------------------------------------------------

/** Convert a numeric scale to its display suffix. */
export function convertScaleToSuffix(scale: number): string {
  if (scale === 1) return "";
  if (scale === 1_000) return "K";
  if (scale === 1_000_000) return "MM";
  if (scale === 1_000_000_000) return "B";
  return "";
}

/**
 * Determine the appropriate scale for a numeric value.
 * ≥1B → 1_000_000_000, ≥1M → 1_000_000, ≥1K → 1_000, else 1
 */
export function determineScale(
  value: number | string | null | undefined
): number {
  if (value == null || value === "" || value === "-") return 1;
  try {
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return 1;
    const abs = Math.abs(num);
    if (abs >= 1_000_000_000) return 1_000_000_000;
    if (abs >= 1_000_000) return 1_000_000;
    if (abs >= 1_000) return 1_000;
    return 1;
  } catch {
    return 1;
  }
}

/**
 * Format a number according to its metric type.
 * Returns [formattedString, scaleSuffix] matching the Python format_number.
 */
export function formatNumber(
  value: number | string | null | undefined,
  metricsName: string,
  scale?: number
): [string, string] {
  if (value == null || value === "" || value === "-") return ["-", ""];

  if (scale == null) {
    scale = determineScale(value);
  }
  const suffix = convertScaleToSuffix(scale);

  try {
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return [String(value), ""];

    if (
      ["revenue_usd", "video_revenue_usd", "static_revenue_usd"].includes(
        metricsName
      )
    ) {
      return [
        `$${formatWithCommas(num / scale, 2)}`,
        suffix ? `(${suffix})` : "",
      ];
    }
    if (
      ["revenue_loc", "video_revenue_loc", "static_revenue_loc"].includes(
        metricsName
      )
    ) {
      return [formatWithCommas(num / scale, 2), suffix ? `(${suffix})` : ""];
    }
    if (metricsName === "cpc") {
      return [`$${num.toFixed(2)}`, ""];
    }
    if (metricsName === "cpc_loc") {
      return [num.toFixed(2), ""];
    }
    if (["ad_ctr", "page_ad_ctr"].includes(metricsName)) {
      return [`${(num * 100).toFixed(2)}%`, ""];
    }
    if (
      ["coverage", "page_coverage", "widget_coverage"].includes(metricsName)
    ) {
      return [`${(num * 100).toFixed(2)}%`, ""];
    }
    if (metricsName === "ad_count") {
      return [num.toFixed(2), ""];
    }
    if (["page_ad_count", "ad_density"].includes(metricsName)) {
      return [num.toFixed(2), ""];
    }
    if (metricsName === "rpom") {
      return [`$${num.toFixed(2)}`, ""];
    }
    if (metricsName === "rpom_loc") {
      return [num.toFixed(2), ""];
    }
    if (["total_page_requests", "traffic"].includes(metricsName)) {
      return [formatWithCommas(num / scale, 2), suffix ? `(${suffix})` : ""];
    }
    if (metricsName === "diff_pct") {
      if (num > 0) {
        const display = num < 10 ? num * 100 : 999.9;
        return [`+${display.toFixed(1)}%`, ""];
      } else {
        const display = num > -10 ? num * 100 : -999.9;
        return [`${display.toFixed(1)}%`, ""];
      }
    }
    if (metricsName === "bridge") {
      if (num >= 0) {
        return [`$${(num / scale).toFixed(2)}`, suffix ? `(${suffix})` : ""];
      } else {
        return [
          `-$${(Math.abs(num) / scale).toFixed(2)}`,
          suffix ? `(${suffix})` : "",
        ];
      }
    }
    return [String(num), ""];
  } catch {
    return [String(value), ""];
  }
}

/** Format a number with commas and fixed decimal places. */
function formatWithCommas(num: number, decimals: number): string {
  return num.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// ---------------------------------------------------------------------------
// Diff & Bridge Calculations
// ---------------------------------------------------------------------------

/**
 * Calculate the difference between two values.
 * type='%' → percentage change (value2 - value1) / value1
 * type='abs' → absolute difference value2 - value1
 */
export function getDiff(
  value1: number | string | null | undefined,
  value2: number | string | null | undefined,
  type: "%" | "abs" = "%"
): number | null {
  if (
    value1 == null ||
    value2 == null ||
    value1 === "" ||
    value2 === "" ||
    value1 === "-" ||
    value2 === "-"
  ) {
    return null;
  }
  try {
    const v1 = typeof value1 === "string" ? parseFloat(value1) : value1;
    const v2 = typeof value2 === "string" ? parseFloat(value2) : value2;
    if (isNaN(v1) || isNaN(v2)) return null;
    if (type === "%") {
      if (v1 === 0) return null;
      return (v2 - v1) / v1;
    }
    return v2 - v1;
  } catch {
    return null;
  }
}

/**
 * Bridge decomposition calculation.
 *
 * Computes the dollar contribution and percentage contribution of a subset
 * to the total revenue change, using log-share apportionment for the
 * metric-specific component.
 *
 * Returns [contributionAbs, contributionPct] or [null, null] on invalid input.
 */
export function getBridge(
  totalValue: number | string | null | undefined,
  totalChangePct: number | string | null | undefined,
  subsetValue: number | string | null | undefined,
  subsetChangePct: number | string | null | undefined,
  subsetMetricsChangePct: number | string | null | undefined
): [number | null, number | null] {
  const toNum = (val: number | string | null | undefined): number | null => {
    if (
      val == null ||
      val === "" ||
      val === "-" ||
      val === "NA" ||
      val === "N/A"
    )
      return null;
    const s = String(val);
    if (s.endsWith("%")) {
      const n = parseFloat(s.slice(0, -1));
      return isNaN(n) ? null : n / 100;
    }
    const n = parseFloat(s);
    return isNaN(n) ? null : n;
  };

  try {
    const tv = toNum(totalValue);
    const tcp = toNum(totalChangePct);
    const sv = toNum(subsetValue);
    const scp = toNum(subsetChangePct);
    let smcp = toNum(subsetMetricsChangePct);

    if (tv == null || tcp == null || sv == null || scp == null)
      return [null, null];
    if (tcp === 0) return [null, null];

    const totalChange = (tv / (1 + tcp)) * tcp;
    if (totalChange === 0) return [null, null];

    if (scp === 0) return [0, 0];

    const subsetChange = (sv / (1 + scp)) * scp;
    const subsetContributionPct = subsetChange / totalChange;
    const subsetContribution = totalChange * subsetContributionPct;

    if (smcp == null) smcp = scp;

    // Guard log domain: 1 + x must be > 0
    if (1 + smcp <= 0 || 1 + scp <= 0) return [null, null];

    const subsetMetricsContributionToSubsetPct =
      Math.log(1 + smcp) / Math.log(1 + scp);
    const subsetMetricsContribution =
      subsetContribution * subsetMetricsContributionToSubsetPct;
    const subsetMetricsContributionPct =
      (subsetMetricsContribution / totalChange) * tcp;

    return [subsetMetricsContribution, subsetMetricsContributionPct];
  } catch {
    return [null, null];
  }
}

// ---------------------------------------------------------------------------
// Data Structure Helpers
// ---------------------------------------------------------------------------

/** Parse pre-processed data rows into an indexed lookup by (geo, pageType, deviceType, dateGroup). */
export function getDataIndex(data: DataRow[]): DataIndex {
  const index: DataIndex = {};
  for (const row of data) {
    // Uppercase geo for case-insensitive matching (API returns "WORLDWIDE", RDS returns "Worldwide")
    const geo = (row.geo || "").toUpperCase();
    const key = `${geo}|${row.page_type}|${row.device_type}|${row.date_group}`;
    index[key] = row;
  }
  return index;
}

/** Get dimension ordering for a product type. */
export function getDimOrder(product: string): DimOrder {
  if (product === "SP") {
    return {
      "Search+Detail+Homepage": [["", "Total", "show"]],
      Search: [
        ["", "Total", "show"],
        ["p1_", "Page1", "hide"],
        ["top_p1_", "Page1-Native-TOP", "show"],
        ["mop_p1_", "Page1-Native-MOP", "show"],
        ["bop_p1_", "Page1-Native-BOP", "show"],
        ["spt_p1_inline_", "Page1-Widget-Search_Thematic_Inline", "show"],
        ["spt_p1_btf_", "Page1-Widget-Search_Thematic_Bottom", "show"],
        ["p2_", "Page2", "hide"],
        ["top_p2_", "Page2-Native-TOP", "show"],
        ["mop_p2_", "Page2-Native-MOP", "show"],
        ["bop_p2_", "Page2-Native-BOP", "show"],
        ["spt_p2_inline_", "Page2-Widget-Search_Thematic_Inline", "show"],
        ["spt_p2_btf_", "Page2-Widget-Search_Thematic_Bottom", "show"],
      ],
      Detail: [
        ["", "Total", "show"],
        ["dp1_", "DP1", "show"],
        ["dpt_", "DP_Thematic", "show"],
        ["dp2_", "DP2", "show"],
        ["hqp_", "HQP", "show"],
        ["ilm_", "ILM", "show"],
      ],
      Homepage: [["", "Total", "show"]],
    };
  }
  // SB
  return {
    "Search+Detail": [["", "Total", "show"]],
    Search: [
      ["", "Total", "show"],
      ["total_sparkle_", "Total_Sparkle", "show"],
      ["p1_", "Page1", "hide"],
      ["sparkle_p1_", "Page1-Sparkle", "show"],
      ["inline_p1_", "Page1-Inline", "show"],
      ["bottom_p1_", "Page1-Bottom", "show"],
      ["3psl_p1_", "Page1-3PSL", "show"],
      ["p2_", "Page2", "hide"],
      ["sparkle_p2_", "Page2-Sparkle", "show"],
      ["inline_p2_", "Page2-Inline", "show"],
      ["bottom_p2_", "Page2-Bottom", "show"],
      ["3psl_p2_", "Page2-3PSL", "show"],
    ],
    Detail: [
      ["", "Total", "show"],
      ["brand_zone_", "Brand_Zone", "show"],
      ["ilm_", "ILM", "show"],
      ["hqp_", "HQP", "show"],
      ["other_dp_", "Other_DP", "show"],
    ],
  };
}

/** Get metrics ordering for a product type. */
export function getMetricsOrder(product: string): MetricDef[] {
  if (product === "SP") {
    return [
      ["revenue_usd", "Revenue USD"],
      ["revenue_loc", "Revenue LOC"],
      ["total_page_requests", "Page Views"],
      ["rpom", "RPOM USD"],
      ["rpom_loc", "RPOM LOC"],
      ["coverage", "Coverage"],
      ["cpc", "CPC USD"],
      ["cpc_loc", "CPC LOC"],
      ["ad_count", "Ad Count"],
      ["ad_ctr", "Ad CTR"],
    ];
  }
  // SB
  return [
    ["revenue_usd", "Revenue USD"],
    ["video_revenue_usd", "Video Revenue USD"],
    ["static_revenue_usd", "Static Revenue USD"],
    ["revenue_loc", "Revenue LOC"],
    ["video_revenue_loc", "Video Revenue LOC"],
    ["static_revenue_loc", "Static Revenue LOC"],
    ["traffic", "Page Views"],
    ["rpom", "RPOM USD"],
    ["rpom_loc", "RPOM LOC"],
    ["coverage", "Coverage"],
    ["ad_count", "Ad Count"],
    ["cpc", "CPC USD"],
    ["cpc_loc", "CPC LOC"],
    ["ad_ctr", "Ad CTR"],
  ];
}

/** Get placement hierarchy for bridge rollups. */
export function getPlacementHierarchy(product: string): PlacementHierarchy {
  if (product === "SP") {
    return {
      Search: {
        p1: ["top_p1", "mop_p1", "bop_p1", "spt_p1_inline", "spt_p1_btf"],
        p2: ["top_p2", "mop_p2", "bop_p2", "spt_p2_inline", "spt_p2_btf"],
      },
      Detail: ["dp1", "dpt", "dp2", "hqp", "ilm"],
      Homepage: [],
    };
  }
  // SB
  return {
    Search: {
      p1: ["sparkle_p1", "inline_p1", "bottom_p1", "3psl_p1"],
      p2: ["sparkle_p2", "inline_p2", "bottom_p2", "3psl_p2"],
    },
    Detail: ["brand_zone", "ilm", "hqp", "other_dp"],
  };
}

/** Get the correct metric column name based on product and dimension prefix. */
function getMetricName(
  metricName: string,
  dimRaw: string,
  product: string
): string {
  if (metricName === "total_page_requests") {
    if (dimRaw.includes("_p1")) return "total_p1_page_requests";
    if (dimRaw.includes("_p2")) return "total_p2_page_requests";
    return "total_page_requests";
  }

  // Homepage dim: hp_ prefix maps to hp_<metric> columns
  if (dimRaw === "hp_") {
    const hpMap: Record<string, string> = {
      revenue_usd: "hp_revenue_usd",
      revenue_loc: "hp_revenue_loc",
      rpom: "hp_rpom",
      rpom_loc: "hp_rpom_loc",
      coverage: "hp_coverage",
      cpc: "hp_cpc",
      cpc_loc: "hp_cpc_loc",
      ad_count: "hp_ad_count",
      ad_ctr: "hp_ad_ctr",
    };
    return hpMap[metricName] ?? "";
  }

  // LOC metrics follow the same dimension pattern as their USD counterparts
  // NOTE: this must come AFTER SB-specific checks (total_sparkle_ etc.) to avoid wrong column names
  if (
    product !== "SB" &&
    (metricName === "rpom_loc" || metricName === "cpc_loc")
  ) {
    return dimRaw + metricName;
  }

  if (product === "SB") {
    // Total Sparkle
    if (dimRaw.startsWith("total_sparkle_")) {
      const sparkleMap: Record<string, string> = {
        revenue_usd: "sparkle_revenue_usd",
        revenue_loc: "sparkle_revenue_loc",
        rpom: "sparkle_rpom",
        rpom_loc: "sparkle_rpom_loc",
        traffic: "traffic",
        coverage: "sparkle_coverage",
        cpc: "sparkle_cpc",
        cpc_loc: "sparkle_cpc_loc",
        ad_ctr: "sparkle_ad_ctr",
        ad_density: "sparkle_ad_density",
        ad_count: "sparkle_ad_density",
        static_revenue_usd: "static_sparkle_revenue_usd",
        video_revenue_usd: "video_sparkle_revenue_usd",
        static_revenue_loc: "static_sparkle_revenue_loc",
        video_revenue_loc: "video_sparkle_revenue_loc",
      };
      return sparkleMap[metricName] ?? "";
    }

    // Static/Video revenue (USD and LOC)
    if (
      [
        "static_revenue_usd",
        "video_revenue_usd",
        "static_revenue_loc",
        "video_revenue_loc",
      ].includes(metricName)
    ) {
      if (dimRaw === "") return metricName;
      const isLoc = metricName.endsWith("_loc");
      const prefix = metricName.startsWith("static") ? "static" : "video";
      const suffix = isLoc ? "revenue_loc" : "revenue_usd";
      if (dimRaw.includes("_p1")) {
        let placement = dimRaw.replace("_p1_", "").replace(/_$/, "");
        return placement
          ? `${prefix}_${placement}_p1_${suffix}`
          : `${prefix}_p1_${suffix}`;
      }
      if (dimRaw.includes("_p2")) {
        let placement = dimRaw.replace("_p2_", "").replace(/_$/, "");
        return placement
          ? `${prefix}_${placement}_p2_${suffix}`
          : `${prefix}_p2_${suffix}`;
      }
      // Detail placements
      if (dimRaw.startsWith("hqp_")) return `${prefix}_hqp_${suffix}`;
      if (dimRaw.startsWith("other_dp_")) return `${prefix}_other_dp_${suffix}`;
      if (dimRaw.startsWith("brand_zone_"))
        return `${prefix}_brand_zone_${suffix}`;
      if (dimRaw.startsWith("ilm_")) return `${prefix}_ilm_${suffix}`;
      return dimRaw + metricName;
    }

    // Traffic
    if (metricName === "traffic") {
      if (dimRaw === "" || dimRaw.startsWith("total_sparkle")) return "traffic";
      if (dimRaw.includes("_p1")) return "p1_traffic";
      if (dimRaw.includes("_p2")) return "p2_traffic";
      return "traffic";
    }

    // Ad Count
    if (metricName === "ad_count") {
      if (dimRaw === "") return "page_ad_count";
      if (dimRaw.startsWith("total_sparkle")) return "sparkle_ad_density";
      const adCountMap: Record<string, string> = {
        sparkle_p1_: "sparkle_p1_ad_density",
        inline_p1_: "inline_p1_ad_density",
        bottom_p1_: "bottom_p1_ad_density",
        "3psl_p1_": "3psl_p1_ad_density",
        sparkle_p2_: "sparkle_p2_ad_density",
        inline_p2_: "inline_p2_ad_density",
        bottom_p2_: "bottom_p2_ad_density",
        "3psl_p2_": "3psl_p2_ad_density",
        brand_zone_: "brand_zone_ad_density",
        ilm_: "ilm_ad_density",
        hqp_: "hqp_ad_density",
        other_dp_: "other_dp_ad_density",
      };
      return adCountMap[dimRaw] ?? "page_ad_count";
    }

    // Ad CTR — Total / P1 / P2+ rollups use page_ad_ctr (clicks / page_imp).
    // Placement-level rows keep ad-impression CTR.
    if (metricName === "ad_ctr") {
      if (dimRaw === "") return "page_ad_ctr";
      if (dimRaw === "total_sparkle_") return "sparkle_ad_ctr";
      if (dimRaw === "p1_") return "p1_page_ad_ctr";
      if (dimRaw === "p2_") return "p2_page_ad_ctr";
      if (dimRaw.includes("_p1")) {
        const placement = dimRaw.replace("_p1_", "").replace(/_$/, "");
        return placement ? `${placement}_p1_ad_ctr` : "p1_page_ad_ctr";
      }
      if (dimRaw.includes("_p2")) {
        const placement = dimRaw.replace("_p2_", "").replace(/_$/, "");
        return placement ? `${placement}_p2_ad_ctr` : "p2_page_ad_ctr";
      }
      return dimRaw + "ad_ctr";
    }

    // Coverage, CPC, page_coverage, page_ad_count, ad_density
    if (
      [
        "cpc",
        "page_coverage",
        "coverage",
        "page_ad_count",
        "ad_density",
      ].includes(metricName)
    ) {
      if (dimRaw === "") return metricName;
      if (dimRaw.includes("_p1")) {
        const placement = dimRaw.replace("_p1_", "").replace(/_$/, "");
        return placement ? `${placement}_p1_${metricName}` : `p1_${metricName}`;
      }
      if (dimRaw.includes("_p2")) {
        const placement = dimRaw.replace("_p2_", "").replace(/_$/, "");
        return placement ? `${placement}_p2_${metricName}` : `p2_${metricName}`;
      }
      return dimRaw + metricName;
    }

    return dimRaw + metricName;
  }

  // SP: simple prefix concatenation
  return dimRaw + metricName;
}

/** Safely compare a value to a comparison value. Returns true if value > comparison. */
function safeNumericCompare(
  value: number | string | null | undefined,
  comparisonValue: number = 0
): boolean {
  if (value == null || value === "") return false;
  try {
    const num = typeof value === "string" ? parseFloat(value) : value;
    return !isNaN(num) && num > comparisonValue;
  } catch {
    return false;
  }
}

/** Get CSS class for positive/negative values. */
function getValueClass(value: number | string | null | undefined): string {
  if (safeNumericCompare(value, 0)) return "positive";
  if (safeNumericCompare(0, value as number)) return "negative";
  return "";
}

/** Sum a list of numeric values, treating non-numbers as 0. */
function sumList(values: (number | string | null | undefined)[]): number {
  let total = 0;
  for (const v of values) {
    if (typeof v === "number" && !isNaN(v)) total += v;
  }
  return total;
}

/** Safe conversion to number, returning null for invalid values. */
function toNum(val: number | string | null | undefined): number | null {
  if (val == null || val === "" || val === "-" || val === "NA" || val === "N/A")
    return null;
  const n = typeof val === "number" ? val : parseFloat(String(val));
  return isNaN(n) ? null : n;
}

// ---------------------------------------------------------------------------
// Table Header Generation
// ---------------------------------------------------------------------------

function generateTableHeaders(product: string): TableHeader[][] {
  if (product === "SP") {
    return [
      [
        { label: "", rowspan: 4 },
        { label: "Search\n& Detail\n& Homepage", rowspan: 4 },
        { label: "Search", colspan: 11 },
        { label: "Detail", colspan: 6 },
        { label: "Homepage", colspan: 1 },
      ],
      [
        { label: "Total Search", rowspan: 3 },
        { label: "Page 1", colspan: 5 },
        { label: "Page 2+", colspan: 5 },
        { label: "Total Detail", rowspan: 3 },
        { label: "DP1", rowspan: 3 },
        { label: "DP Thematic", rowspan: 3 },
        { label: "DP2", rowspan: 3 },
        { label: "HQP", rowspan: 3 },
        { label: "ILM", rowspan: 3 },
        { label: "Total Homepage", rowspan: 3 },
      ],
      [
        { label: "Native", colspan: 3 },
        { label: "Widget", colspan: 2 },
        { label: "Native", colspan: 3 },
        { label: "Widget", colspan: 2 },
      ],
      [
        { label: "TOP" },
        { label: "MOP" },
        { label: "BOP" },
        { label: "Inline" },
        { label: "Bottom" },
        { label: "TOP" },
        { label: "MOP" },
        { label: "BOP" },
        { label: "Inline" },
        { label: "Bottom" },
      ],
    ];
  }
  // SB
  return [
    [
      { label: "", rowspan: 3 },
      { label: "Search\n& Detail", rowspan: 3 },
      { label: "Search", colspan: 10 },
      { label: "Detail", colspan: 5 },
    ],
    [
      { label: "Total Search", rowspan: 2 },
      { label: "Total Sparkle", rowspan: 2 },
      { label: "Page 1", colspan: 4 },
      { label: "Page 2+", colspan: 4 },
      { label: "Total Detail", rowspan: 2 },
      { label: "Brand Zone", rowspan: 2 },
      { label: "ILM", rowspan: 2 },
      { label: "HQP", rowspan: 2 },
      { label: "Other DP", rowspan: 2 },
    ],
    [
      { label: "Sparkle" },
      { label: "Inline" },
      { label: "Bottom" },
      { label: "3PSL" },
      { label: "Sparkle" },
      { label: "Inline" },
      { label: "Bottom" },
      { label: "3PSL" },
    ],
  ];
}

// ---------------------------------------------------------------------------
// Table Generators
// ---------------------------------------------------------------------------

/** Generate the Business Metrics table. */
function generateMetricsTable(
  product: string,
  targetMainData: DataRow,
  targetSearchData: DataRow,
  targetDetailData: DataRow,
  metricsOrder: MetricDef[],
  dimOrder: DimOrder,
  metricsDict: MetricsDict,
  forceScale?: number,
  targetHomepageData: DataRow = {}
): TableData {
  const headers = generateTableHeaders(product);
  const rows: TableRow[] = [];
  const mainKey = product === "SP" ? "Search+Detail+Homepage" : "Search+Detail";

  for (const metric of metricsOrder) {
    const [metricKey, metricLabel] = metric;
    const cells: TableCell[] = [];
    let suffix = "";

    const groups = Object.keys(dimOrder) as Array<keyof typeof dimOrder>;
    for (const group of groups) {
      for (const dim of dimOrder[group]) {
        const [dimRaw, , dimDisplay] = dim;
        const metricsName = getMetricName(metricKey, dimRaw, product);

        let rawValue: string | number = "";
        if (group === mainKey) rawValue = targetMainData[metricsName] ?? "";
        else if (group === "Search")
          rawValue = targetSearchData[metricsName] ?? "";
        else if (group === "Detail")
          rawValue = targetDetailData[metricsName] ?? "";
        else if (group === "Homepage")
          rawValue = targetHomepageData[metricsName] ?? "";

        metricsDict[`${group}+${dimRaw}+${metricKey}+actual`] = rawValue;

        const totalRevenue = metricsDict[`${mainKey}++revenue_usd+actual`];
        const scale =
          forceScale ?? determineScale(totalRevenue as number | string);

        const [formatted, sfx] = formatNumber(rawValue, metricKey, scale);
        if (sfx) suffix = sfx;

        if (dimDisplay === "show") {
          const numVal = toNum(rawValue);
          cells.push({ value: formatted, cssClass: "", rawValue: numVal });
        }
      }
    }

    let label = metricLabel;
    if (suffix && !metricLabel.includes(suffix)) {
      label = `${metricLabel} ${suffix}`;
    }
    rows.push({ label, cells });
  }

  return { headers, rows };
}

/** Generate the Metric Changes table. */
function generateChangesTable(
  product: string,
  targetMainData: DataRow,
  baselineMainData: DataRow,
  targetSearchData: DataRow,
  baselineSearchData: DataRow,
  targetDetailData: DataRow,
  baselineDetailData: DataRow,
  metricsOrder: MetricDef[],
  dimOrder: DimOrder,
  metricsDict: MetricsDict,
  targetHomepageData: DataRow = {},
  baselineHomepageData: DataRow = {}
): TableData {
  const headers = generateTableHeaders(product);
  const rows: TableRow[] = [];
  const mainKey = product === "SP" ? "Search+Detail+Homepage" : "Search+Detail";

  for (const metric of metricsOrder) {
    const [metricKey, metricLabel] = metric;
    const cells: TableCell[] = [];

    const groups = Object.keys(dimOrder) as Array<keyof typeof dimOrder>;
    for (const group of groups) {
      for (const dim of dimOrder[group]) {
        const [dimRaw, , dimDisplay] = dim;
        const metricsName = getMetricName(metricKey, dimRaw, product);

        let targetValue = "";
        let baselineValue = "";
        if (group === mainKey) {
          targetValue = targetMainData[metricsName] ?? "0";
          baselineValue = baselineMainData[metricsName] ?? "0";
        } else if (group === "Search") {
          targetValue = targetSearchData[metricsName] ?? "0";
          baselineValue = baselineSearchData[metricsName] ?? "0";
        } else if (group === "Detail") {
          targetValue = targetDetailData[metricsName] ?? "0";
          baselineValue = baselineDetailData[metricsName] ?? "0";
        } else if (group === "Homepage") {
          targetValue = targetHomepageData[metricsName] ?? "0";
          baselineValue = baselineHomepageData[metricsName] ?? "0";
        }

        const rawDiffPct = getDiff(baselineValue, targetValue, "%");
        const [formatted] = formatNumber(rawDiffPct, "diff_pct");
        const cssClass = getValueClass(rawDiffPct);

        metricsDict[`${group}+${dimRaw}+${metricKey}+diff_pct`] =
          rawDiffPct ?? "";

        if (dimDisplay === "show") {
          cells.push({ value: formatted, cssClass, rawValue: rawDiffPct });
        }
      }
    }

    rows.push({ label: metricLabel, cells });
  }

  return { headers, rows };
}

// ---------------------------------------------------------------------------
// Bridge Metrics Computation
// ---------------------------------------------------------------------------

/** Compute bridge and bridge percentage dictionaries for all metrics. */
function generateBridgeMetrics(
  targetMainData: DataRow,
  baselineMainData: DataRow,
  targetSearchData: DataRow,
  baselineSearchData: DataRow,
  targetDetailData: DataRow,
  baselineDetailData: DataRow,
  metricsOrder: MetricDef[],
  dimOrder: DimOrder,
  placementHierarchy: PlacementHierarchy,
  metricsDict: MetricsDict,
  targetHomepageData: DataRow = {},
  baselineHomepageData: DataRow = {}
): { bridgeDict: BridgeDict; bridgePctDict: BridgeDict } {
  const bridgeDict: BridgeDict = {};
  const bridgePctDict: BridgeDict = {};

  const metricKeys = metricsOrder.map((m) => m[0]);
  const isSB =
    metricKeys.includes("video_revenue_usd") ||
    (metricKeys.includes("traffic") && metricKeys.includes("ad_ctr"));
  const adCtrKey = metricKeys.includes("page_ad_ctr")
    ? "page_ad_ctr"
    : "ad_ctr";
  const pageviewsKey = metricKeys.includes("traffic")
    ? "traffic"
    : "total_page_requests";
  const mainKey = isSB ? "Search+Detail" : "Search+Detail+Homepage";
  const totalActualKey = `${mainKey}++revenue_usd+actual`;
  const totalDiffKey = `${mainKey}++revenue_usd+diff_pct`;

  // SB: normalize headline anchors
  if (isSB) {
    try {
      const headlineTotalActual = targetMainData.revenue_usd ?? "0";
      const headlineBaselineActual = baselineMainData.revenue_usd ?? "0";
      const headlineDiffPct = getDiff(
        headlineBaselineActual,
        headlineTotalActual,
        "%"
      );
      metricsDict[totalActualKey] = headlineTotalActual;
      metricsDict[totalDiffKey] = headlineDiffPct ?? "";
    } catch {
      /* ignore */
    }
  }

  // First pass: compute base contributions
  for (const metric of metricsOrder) {
    const metricName = metric[0];
    bridgeDict[metricName] = {
      Search: {},
      Detail: {},
      "Search+Detail": {},
      Homepage: {},
    };
    bridgePctDict[metricName] = {
      Search: {},
      Detail: {},
      "Search+Detail": {},
      Homepage: {},
    };

    for (const group of ["Search", "Detail"] as const) {
      for (const dim of dimOrder[group]) {
        const dimRaw = dim[0];
        if (["", "p1_", "p2_"].includes(dimRaw)) continue;

        const totalValue = metricsDict[totalActualKey];
        const totalChangePct = metricsDict[totalDiffKey];

        let subsetValue =
          metricsDict[`${group}++revenue_usd+actual`] ??
          metricsDict[`${group}+${dimRaw}+revenue_usd+actual`];
        let subsetChangePct =
          metricsDict[`${group}+${dimRaw}+revenue_usd+diff_pct`];

        // For additive components, use component actual and pct change
        if (["video_revenue_usd", "static_revenue_usd"].includes(metricName)) {
          subsetValue = metricsDict[`${group}+${dimRaw}+${metricName}+actual`];
          subsetChangePct =
            metricsDict[`${group}+${dimRaw}+${metricName}+diff_pct`];
        } else {
          subsetValue = metricsDict[`${group}+${dimRaw}+revenue_usd+actual`];
        }

        const metricChangePct =
          metricsDict[`${group}+${dimRaw}+${metricName}+diff_pct`];

        const [rawContribution, rawContributionPct] = getBridge(
          totalValue as number,
          totalChangePct as number,
          subsetValue as number,
          subsetChangePct as number,
          metricChangePct as number
        );

        bridgeDict[metricName][group][dimRaw] = rawContribution ?? "";
        bridgePctDict[metricName][group][dimRaw] = rawContributionPct ?? "";
      }
    }

    // Homepage bridge (SP only) — single total, no placement breakdown
    if (!isSB && dimOrder["Homepage"]) {
      const hpMetricName = getMetricName(metricName, "", "SP");
      const hpTarget = toNum(targetHomepageData[hpMetricName]);
      const hpBaseline = toNum(baselineHomepageData[hpMetricName]);
      const hpRevTarget = toNum(targetHomepageData["hp_revenue_usd"]);
      const hpRevBaseline = toNum(baselineHomepageData["hp_revenue_usd"]);

      const hpRevChangePct =
        hpRevBaseline != null && hpRevBaseline !== 0 && hpRevTarget != null
          ? (hpRevTarget - hpRevBaseline) / hpRevBaseline
          : null;
      const hpMetricChangePct =
        hpBaseline != null && hpBaseline !== 0 && hpTarget != null
          ? (hpTarget - hpBaseline) / hpBaseline
          : null;

      const [hpContrib, hpContribPct] = getBridge(
        metricsDict[totalActualKey] as number,
        metricsDict[totalDiffKey] as number,
        hpRevTarget ?? 0,
        hpRevChangePct ?? 0,
        hpMetricChangePct ?? hpRevChangePct ?? 0
      );
      bridgeDict[metricName]["Homepage"][""] = hpContrib ?? "";
      bridgePctDict[metricName]["Homepage"][""] = hpContribPct ?? "";
    }
  }

  // Second pass (SB only): decompose RPOM into components
  if (isSB) {
    const rpomComponents = new Set(["coverage", "ad_count", "cpc", adCtrKey]);

    for (const group of ["Search", "Detail"] as const) {
      for (const dim of dimOrder[group]) {
        const dimRaw = dim[0];
        if (["", "p1_", "p2_"].includes(dimRaw)) continue;

        const totalValue = toNum(metricsDict[totalActualKey]);
        const totalChangePct = toNum(metricsDict[totalDiffKey]);
        let totalChange = 0;
        if (
          totalValue != null &&
          totalChangePct != null &&
          totalChangePct !== 0
        ) {
          totalChange = (totalValue / (1 + totalChangePct)) * totalChangePct;
        }

        const rpomContribution = toNum(bridgeDict.rpom?.[group]?.[dimRaw]);
        const rpomChangePct = toNum(
          metricsDict[`${group}+${dimRaw}+rpom+diff_pct`]
        );

        let rpomLogDen: number | null = null;
        try {
          if (rpomChangePct != null) rpomLogDen = Math.log(1 + rpomChangePct);
        } catch {
          /* ignore */
        }

        for (const comp of rpomComponents) {
          if (!bridgeDict[comp])
            bridgeDict[comp] = { Search: {}, Detail: {}, "Search+Detail": {} };
          if (!bridgePctDict[comp])
            bridgePctDict[comp] = {
              Search: {},
              Detail: {},
              "Search+Detail": {},
            };

          const compChangePct = toNum(
            metricsDict[`${group}+${dimRaw}+${comp}+diff_pct`]
          );
          let compContribution: number | string = "";
          let compContributionPct: number | string = "";

          try {
            if (
              rpomContribution != null &&
              rpomLogDen != null &&
              rpomLogDen !== 0
            ) {
              if (compChangePct != null) {
                const compLogNum = Math.log(1 + compChangePct);
                const share = compLogNum / rpomLogDen;
                compContribution = rpomContribution * share;
                if (
                  totalChange !== 0 &&
                  totalChangePct != null &&
                  totalChangePct !== 0
                ) {
                  compContributionPct =
                    ((compContribution as number) / totalChange) *
                    totalChangePct;
                }
              }
            }
          } catch {
            /* ignore */
          }

          bridgeDict[comp][group][dimRaw] = compContribution;
          bridgePctDict[comp][group][dimRaw] = compContributionPct;
        }
      }
    }

    // SB anchoring: scale component leaves so headline totals match
    const tv = toNum(metricsDict[totalActualKey]);
    const tcp = toNum(metricsDict[totalDiffKey]);
    const totalChangeVal =
      tv != null && tcp != null && tcp !== 0 ? (tv / (1 + tcp)) * tcp : null;

    const searchLeafDims = [
      ...placementHierarchy.Search.p1.map((p) => `${p}_`),
      ...placementHierarchy.Search.p2.map((p) => `${p}_`),
    ];
    const detailLeafDims = placementHierarchy.Detail.map((p) => `${p}_`);

    const sumLeaves = (metricKey: string, container: BridgeDict): number => {
      let total = 0;
      for (const [groupName, dims] of [
        ["Search", searchLeafDims],
        ["Detail", detailLeafDims],
      ] as const) {
        for (const d of dims) {
          const v = toNum(container[metricKey]?.[groupName]?.[d]);
          if (v != null) total += v;
        }
      }
      return total;
    };

    const currentAbs = sumLeaves("revenue_usd", bridgeDict);
    const currentPct = sumLeaves("revenue_usd", bridgePctDict);

    const ratioAbs =
      totalChangeVal != null && totalChangeVal !== 0 && currentAbs !== 0
        ? totalChangeVal / currentAbs
        : null;
    const ratioPct =
      tcp != null && tcp !== 0 && currentPct !== 0 ? tcp / currentPct : null;

    for (const [groupName, dims] of [
      ["Search", searchLeafDims],
      ["Detail", detailLeafDims],
    ] as const) {
      for (const d of dims) {
        if (ratioAbs != null) {
          for (const comp of ["video_revenue_usd", "static_revenue_usd"]) {
            const v = toNum(bridgeDict[comp]?.[groupName]?.[d]);
            if (v != null) {
              if (!bridgeDict[comp])
                bridgeDict[comp] = {
                  Search: {},
                  Detail: {},
                  "Search+Detail": {},
                };
              bridgeDict[comp][groupName][d] = v * ratioAbs;
            }
          }
        }
        if (ratioPct != null) {
          for (const comp of [
            "revenue_usd",
            "video_revenue_usd",
            "static_revenue_usd",
          ]) {
            const vp = toNum(bridgePctDict[comp]?.[groupName]?.[d]);
            if (vp != null) {
              if (!bridgePctDict[comp])
                bridgePctDict[comp] = {
                  Search: {},
                  Detail: {},
                  "Search+Detail": {},
                };
              bridgePctDict[comp][groupName][d] = vp * ratioPct;
            }
          }
        }
      }
    }

    // Enforce identities for SB slices
    for (const group of ["Search", "Detail"] as const) {
      for (const dim of dimOrder[group]) {
        const dimRaw = dim[0];
        if (["", "p1_", "p2_"].includes(dimRaw)) continue;

        const tvLocal = toNum(metricsDict[totalActualKey]);
        const tcpLocal = toNum(metricsDict[totalDiffKey]);
        let totalChangeLocal: number | null = null;
        if (tvLocal != null && tcpLocal != null && tcpLocal !== 0) {
          totalChangeLocal = (tvLocal / (1 + tcpLocal)) * tcpLocal;
        }

        const v = toNum(bridgeDict.video_revenue_usd?.[group]?.[dimRaw]);
        const s = toNum(bridgeDict.static_revenue_usd?.[group]?.[dimRaw]);
        const t = toNum(bridgeDict[pageviewsKey]?.[group]?.[dimRaw]);
        const r = toNum(bridgeDict.rpom?.[group]?.[dimRaw]);

        // revenue = video + static
        if (v != null || s != null) {
          const rev = (v ?? 0) + (s ?? 0);
          bridgeDict.revenue_usd[group][dimRaw] = rev;
          if (
            totalChangeLocal != null &&
            totalChangeLocal !== 0 &&
            tcpLocal != null &&
            tcpLocal !== 0
          ) {
            bridgePctDict.revenue_usd[group][dimRaw] =
              (rev / totalChangeLocal) * tcpLocal;
          }
        }

        // Adjust rpom to close identity: rpom = revenue - traffic
        const revVal = toNum(bridgeDict.revenue_usd?.[group]?.[dimRaw]);
        if (revVal != null && t != null && r != null) {
          const adjustedRpom = revVal - t;
          bridgeDict.rpom[group][dimRaw] = adjustedRpom;
          if (
            totalChangeLocal != null &&
            totalChangeLocal !== 0 &&
            tcpLocal != null &&
            tcpLocal !== 0
          ) {
            bridgePctDict.rpom[group][dimRaw] =
              (adjustedRpom / totalChangeLocal) * tcpLocal;
          }

          // Rescale RPOM components
          const compKeys = ["coverage", "ad_count", "cpc", adCtrKey];
          const compVals = compKeys.map((k) =>
            toNum(bridgeDict[k]?.[group]?.[dimRaw])
          );
          if (compVals.every((cv) => cv != null)) {
            const currentSum = compVals.reduce(
              (acc: number, cv) => acc + (cv ?? 0),
              0
            );
            if (currentSum !== 0) {
              const ratio = adjustedRpom / currentSum;
              for (let idx = 0; idx < compKeys.length; idx++) {
                const newVal = (compVals[idx] ?? 0) * ratio;
                bridgeDict[compKeys[idx]][group][dimRaw] = newVal;
                if (
                  totalChangeLocal != null &&
                  totalChangeLocal !== 0 &&
                  tcpLocal != null &&
                  tcpLocal !== 0
                ) {
                  bridgePctDict[compKeys[idx]][group][dimRaw] =
                    (newVal / totalChangeLocal) * tcpLocal;
                }
              }
            }
          }
        }
      }
    }
  }

  // Roll up p1_/p2_/totals
  for (const metric of metricsOrder) {
    const key = metric[0];
    if (!bridgeDict[key]) continue;

    // Abs bridge rollups
    bridgeDict[key].Search["p1_"] = sumList(
      placementHierarchy.Search.p1.map((p) => bridgeDict[key].Search[`${p}_`])
    );
    bridgeDict[key].Search["p2_"] = sumList(
      placementHierarchy.Search.p2.map((p) => bridgeDict[key].Search[`${p}_`])
    );
    bridgeDict[key].Search[""] = sumList([
      bridgeDict[key].Search["p1_"],
      bridgeDict[key].Search["p2_"],
    ]);
    bridgeDict[key].Detail[""] = sumList(
      placementHierarchy.Detail.map((p) => bridgeDict[key].Detail[`${p}_`])
    );
    if (!bridgeDict[key]["Search+Detail"])
      bridgeDict[key]["Search+Detail"] = {};
    bridgeDict[key]["Search+Detail"][""] = sumList([
      bridgeDict[key].Search[""],
      bridgeDict[key].Detail[""],
      bridgeDict[key].Homepage?.[""] ?? 0,
    ]);

    // Pct bridge rollups
    bridgePctDict[key].Search["p1_"] = sumList(
      placementHierarchy.Search.p1.map(
        (p) => bridgePctDict[key].Search[`${p}_`]
      )
    );
    bridgePctDict[key].Search["p2_"] = sumList(
      placementHierarchy.Search.p2.map(
        (p) => bridgePctDict[key].Search[`${p}_`]
      )
    );
    bridgePctDict[key].Search[""] = sumList([
      bridgePctDict[key].Search["p1_"],
      bridgePctDict[key].Search["p2_"],
    ]);
    bridgePctDict[key].Detail[""] = sumList(
      placementHierarchy.Detail.map((p) => bridgePctDict[key].Detail[`${p}_`])
    );
    if (!bridgePctDict[key]["Search+Detail"])
      bridgePctDict[key]["Search+Detail"] = {};
    bridgePctDict[key]["Search+Detail"][""] = sumList([
      bridgePctDict[key].Search[""],
      bridgePctDict[key].Detail[""],
      bridgePctDict[key].Homepage?.[""] ?? 0,
    ]);
  }

  // Hard-anchor the headline growth so the rolled-up Search+Detail(+Homepage)
  // total in the Growth Bridge matches the actual YoY % shown in the KPI
  // strip / headline, instead of drifting due to log-share residuals.
  // Residual is absorbed into Homepage for SP (smallest share, least visible
  // distortion) and Detail for SB (no Homepage).
  for (const revKey of ["revenue_usd", "revenue_loc"] as const) {
    const diffKey = `${mainKey}++${revKey}+diff_pct`;
    const targetTcp = toNum(metricsDict[diffKey]);
    const searchTotal = toNum(bridgePctDict[revKey]?.Search?.[""]);
    const detailTotal = toNum(bridgePctDict[revKey]?.Detail?.[""]);
    const homepageTotal = toNum(bridgePctDict[revKey]?.Homepage?.[""]);
    if (targetTcp != null && searchTotal != null && detailTotal != null) {
      if (isSB) {
        const residual = targetTcp - (searchTotal + detailTotal);
        bridgePctDict[revKey].Detail[""] = detailTotal + residual;
      } else {
        const residual =
          targetTcp - (searchTotal + detailTotal + (homepageTotal ?? 0));
        bridgePctDict[revKey].Homepage = bridgePctDict[revKey].Homepage ?? {};
        bridgePctDict[revKey].Homepage[""] = (homepageTotal ?? 0) + residual;
      }
      bridgePctDict[revKey]["Search+Detail"][""] = targetTcp;
    }
  }

  return { bridgeDict, bridgePctDict };
}

/** Generate the Revenue Bridge table. */
function generateBridgeTable(
  product: string,
  metricsOrder: MetricDef[],
  dimOrder: DimOrder,
  bridgeDict: BridgeDict,
  forceScale?: number
): TableData {
  const headers = generateTableHeaders(product);
  const rows: TableRow[] = [];

  for (const metric of metricsOrder) {
    const [metricKey, metricLabel] = metric;
    const cells: TableCell[] = [];

    // Determine scale from Search+Detail+Homepage total bridge (or use forced scale)
    const mainBridgeKey =
      product === "SP" ? "Search+Detail+Homepage" : "Search+Detail";
    const totalBridge =
      bridgeDict.revenue_usd?.[mainBridgeKey]?.[""] ??
      bridgeDict.revenue_usd?.["Search+Detail"]?.[""] ??
      0;
    const scale = forceScale ?? determineScale(totalBridge as number | string);
    const suffix = convertScaleToSuffix(scale);

    const groups = Object.keys(dimOrder) as Array<keyof typeof dimOrder>;
    for (const group of groups) {
      for (const dim of dimOrder[group]) {
        const [dimRaw, , dimDisplay] = dim;
        // Bridge dict uses "Search+Detail" as rollup key internally; map main group key
        const bridgeGroup = group === mainBridgeKey ? "Search+Detail" : group;
        const rawContribution =
          bridgeDict[metricKey]?.[bridgeGroup]?.[dimRaw] ?? "";
        const [formatted] = formatNumber(
          rawContribution as number,
          "bridge",
          scale
        );
        let cssClass = getValueClass(rawContribution as number);

        if (
          group === mainBridgeKey &&
          dimRaw === "" &&
          metricKey === "revenue_usd"
        ) {
          cssClass += " highlight";
        }

        if (dimDisplay === "show") {
          const numVal = toNum(rawContribution);
          cells.push({
            value: formatted,
            cssClass: cssClass.trim(),
            rawValue: numVal,
          });
        }
      }
    }

    const label = suffix ? `${metricLabel} (${suffix})` : metricLabel;
    rows.push({ label, cells });
  }

  return { headers, rows };
}

/** Generate the Growth Bridge table. */
function generateGrowthTable(
  product: string,
  metricsOrder: MetricDef[],
  dimOrder: DimOrder,
  bridgePctDict: BridgeDict
): TableData {
  const headers = generateTableHeaders(product);
  const rows: TableRow[] = [];

  for (const metric of metricsOrder) {
    const [metricKey, metricLabel] = metric;
    const cells: TableCell[] = [];

    // SB rounding residual for revenue row
    let residualPp = 0;
    let detailLeavesResidualPp = 0;
    let detailLastLeafDim: string | null = null;

    if (product === "SB" && metricKey === "revenue_usd") {
      try {
        const totalRaw = toNum(
          bridgePctDict[metricKey]?.["Search+Detail"]?.[""]
        );
        const searchRaw = toNum(bridgePctDict[metricKey]?.Search?.[""]);
        const detailRaw = toNum(bridgePctDict[metricKey]?.Detail?.[""]);

        const toPp = (x: number | null): number | null =>
          x != null ? Math.round(x * 100 * 10) / 10 : null;

        const totalPp = toPp(totalRaw);
        const searchPp = toPp(searchRaw);
        const detailPp = toPp(detailRaw);

        if (totalPp != null && searchPp != null && detailPp != null) {
          residualPp = totalPp - (searchPp + detailPp);
        }

        // Detail leaves residual
        const detailLeafDims = dimOrder.Detail.filter((d) => d[0] !== "").map(
          (d) => d[0]
        );
        detailLastLeafDim =
          detailLeafDims.length > 0
            ? detailLeafDims[detailLeafDims.length - 1]
            : null;
        const leafPpValues: number[] = [];
        let allPresent = true;
        for (const d of detailLeafDims) {
          const v = toNum(bridgePctDict[metricKey]?.Detail?.[d]);
          const pv = toPp(v);
          if (pv == null) allPresent = false;
          leafPpValues.push(pv ?? 0);
        }
        if (allPresent && detailPp != null) {
          detailLeavesResidualPp =
            detailPp - leafPpValues.reduce((a, b) => a + b, 0);
        }
      } catch {
        /* ignore */
      }
    }

    const mainBridgeKey =
      product === "SP" ? "Search+Detail+Homepage" : "Search+Detail";

    for (const group of Object.keys(dimOrder) as Array<keyof typeof dimOrder>) {
      for (const dim of dimOrder[group]) {
        const [dimRaw, , dimDisplay] = dim;
        const bridgeGroup = group === mainBridgeKey ? "Search+Detail" : group;
        let rawContributionPct: number | string =
          bridgePctDict[metricKey]?.[bridgeGroup]?.[dimRaw] ?? "";

        // SB residual adjustments
        if (
          product === "SB" &&
          metricKey === "revenue_usd" &&
          group === "Detail" &&
          dimRaw === "" &&
          residualPp !== 0
        ) {
          const baseVal = toNum(rawContributionPct) ?? 0;
          rawContributionPct = baseVal + residualPp / 100;
        }
        if (
          product === "SB" &&
          metricKey === "revenue_usd" &&
          group === "Detail" &&
          dimRaw === detailLastLeafDim &&
          detailLeavesResidualPp !== 0
        ) {
          const baseVal = toNum(rawContributionPct) ?? 0;
          rawContributionPct = baseVal + detailLeavesResidualPp / 100;
        }

        const [formatted] = formatNumber(
          rawContributionPct as number,
          "diff_pct"
        );
        let cssClass = getValueClass(rawContributionPct as number);

        if (
          group === mainBridgeKey &&
          dimRaw === "" &&
          metricKey === "revenue_usd"
        ) {
          cssClass += " highlight";
        }

        if (dimDisplay === "show") {
          const numVal = toNum(rawContributionPct);
          cells.push({
            value: formatted,
            cssClass: cssClass.trim(),
            rawValue: numVal,
          });
        }
      }
    }

    rows.push({ label: metricLabel, cells });
  }

  return { headers, rows };
}

// ---------------------------------------------------------------------------
// Main Entry Point
// ---------------------------------------------------------------------------

/** Metadata for report generation. */
export interface AnalysisMetadata {
  event_content: {
    page_type: string;
    device: string;
    target_date_range: [string, string];
    baseline_date_range: [string, string];
  };
  latest_dataset_date?: string;
}

/**
 * Generate a complete report from pre-processed data.
 *
 * This is the main entry point — equivalent to `generate_html_from_data` in
 * the Python implementation, but returns structured ReportData instead of HTML.
 *
 * @param targetData  - Array of data rows for the target period
 * @param baselineData - Array of data rows for the baseline period
 * @param metadata    - Analysis metadata (dates, page type, device)
 * @param geoName     - Geo group name (e.g. 'US', 'WW')
 * @param product     - Product type ('SP' or 'SB')
 */
export function generateReportFromData(
  targetData: DataRow[],
  baselineData: DataRow[],
  metadata: AnalysisMetadata,
  geoName: string,
  product: string,
  forceScale?: number,
  forceBridgeScale?: number
): ReportData {
  const pageType = metadata.event_content.page_type;
  const deviceType = metadata.event_content.device;

  // Index the data for quick lookup
  const targetIndex = getDataIndex(targetData);
  const baselineIndex = getDataIndex(baselineData);

  // Uppercase geoName to match the index (API returns "WORLDWIDE", RDS returns "Worldwide")
  const geoKey = geoName.toUpperCase();

  // Extract data slices
  const targetMainData =
    targetIndex[`${geoKey}|${pageType}|${deviceType}|target`] ?? {};
  const targetSearchData =
    targetIndex[`${geoKey}|Search|${deviceType}|target`] ?? {};
  const targetDetailData =
    targetIndex[`${geoKey}|Detail|${deviceType}|target`] ?? {};
  const targetHomepageData =
    product === "SP"
      ? targetIndex[`${geoKey}|Homepage|${deviceType}|target`] ?? {}
      : {};
  const baselineMainData =
    baselineIndex[`${geoKey}|${pageType}|${deviceType}|baseline`] ?? {};
  const baselineSearchData =
    baselineIndex[`${geoKey}|Search|${deviceType}|baseline`] ?? {};
  const baselineDetailData =
    baselineIndex[`${geoKey}|Detail|${deviceType}|baseline`] ?? {};
  const baselineHomepageData =
    product === "SP"
      ? baselineIndex[`${geoKey}|Homepage|${deviceType}|baseline`] ?? {}
      : {};

  const dimOrder = getDimOrder(product);
  const metricsOrder = getMetricsOrder(product);
  const placementHierarchy = getPlacementHierarchy(product);

  // Shared metrics dictionary for cross-table calculations
  const metricsDict: MetricsDict = {};

  // Generate tables in order (metrics → changes → bridge → growth)
  const metricsTable = generateMetricsTable(
    product,
    targetMainData,
    targetSearchData,
    targetDetailData,
    metricsOrder,
    dimOrder,
    metricsDict,
    forceScale,
    targetHomepageData
  );

  const changesTable = generateChangesTable(
    product,
    targetMainData,
    baselineMainData,
    targetSearchData,
    baselineSearchData,
    targetDetailData,
    baselineDetailData,
    metricsOrder,
    dimOrder,
    metricsDict,
    targetHomepageData,
    baselineHomepageData
  );

  const { bridgeDict, bridgePctDict } = generateBridgeMetrics(
    targetMainData,
    baselineMainData,
    targetSearchData,
    baselineSearchData,
    targetDetailData,
    baselineDetailData,
    metricsOrder,
    dimOrder,
    placementHierarchy,
    metricsDict,
    targetHomepageData,
    baselineHomepageData
  );

  const bridgeTable = generateBridgeTable(
    product,
    metricsOrder,
    dimOrder,
    bridgeDict,
    forceBridgeScale // use primary's bridge scale for comparison; undefined = auto-determine
  );
  const growthTable = generateGrowthTable(
    product,
    metricsOrder,
    dimOrder,
    bridgePctDict
  );

  // Compute KPI summary cards (matching old VESL dashboard)
  const revenue: number = Number(targetMainData.revenue_usd) || 0;
  const pvKey = product === "SB" ? "traffic" : "total_page_requests";
  const pageviews: number = Number(targetMainData[pvKey]) || 0;
  const kpiRpom: number = Number(targetMainData.rpom) || 0;
  const baseRevenue: number = Number(baselineMainData.revenue_usd) || 0;
  const basePV: number = Number(baselineMainData[pvKey]) || 0;
  const baseRPOM: number = Number(baselineMainData.rpom) || 0;
  const totalRev =
    metricsDict[
      `${
        product === "SP" ? "Search+Detail+Homepage" : "Search+Detail"
      }++revenue_usd+actual`
    ];
  const scale = forceScale ?? determineScale(totalRev as number | string);

  // Per-metric scales (VESL uses independent scales for each KPI)
  const revScale = forceScale ?? determineScale(revenue);
  const pvScale = forceScale ?? determineScale(pageviews);

  const pctFmt = (cur: number, prev: number) => {
    if (!prev) return { text: "", pos: true };
    const pct = ((cur - prev) / Math.abs(prev)) * 100;
    const sign = pct >= 0 ? "+" : "";
    return { text: `${sign}${pct.toFixed(1)}%`, pos: pct >= 0 };
  };
  const revChg = pctFmt(revenue, baseRevenue);
  const pvChg = pctFmt(pageviews, basePV);
  const rpomChg = pctFmt(kpiRpom, baseRPOM);
  const kpiSummary: KPISummary[] = [
    {
      label: "Total Revenue",
      value: `${formatNumber(revenue, "revenue_usd", revScale)[0]} ${
        formatNumber(revenue, "revenue_usd", revScale)[1]
      }`.trim(),
      change: revChg.text,
      positive: revChg.pos,
    },
    {
      label: "Page Views",
      value: `${formatNumber(pageviews, pvKey, pvScale)[0]} ${
        formatNumber(pageviews, pvKey, pvScale)[1]
      }`.trim(),
      change: pvChg.text,
      positive: pvChg.pos,
    },
    {
      label: "RPOM",
      value: formatNumber(kpiRpom, "rpom", 1)[0],
      change: rpomChg.text,
      positive: rpomChg.pos,
    },
  ];

  // Derive bridge scale from the bridge table's revenue row label suffix
  const bridgeRevRow = bridgeTable.rows.find((r) =>
    r.label.startsWith("Revenue USD")
  );
  const bridgeScaleMatch = bridgeRevRow?.label.match(/\(([^)]+)\)\s*$/);
  const bridgeScaleSuffix = bridgeScaleMatch?.[1]?.toUpperCase();
  const bridgeScale =
    bridgeScaleSuffix === "B"
      ? 1e9
      : bridgeScaleSuffix === "MM"
      ? 1e6
      : bridgeScaleSuffix === "K"
      ? 1e3
      : 1;

  return {
    metricsTable,
    changesTable,
    bridgeTable,
    growthTable,
    kpiSummary,
    scale,
    bridgeScale,
  };
}

// ---------------------------------------------------------------------------
// Difference Table Computation (Task 6.2)
// ---------------------------------------------------------------------------

/**
 * Compute cell-wise difference between two ReportData objects.
 *
 * For each corresponding cell pair:
 * - If either rawValue is null -> result is null
 * - Otherwise -> result = primary.rawValue - comparison.rawValue
 *
 * Formats diffs using the same scale as the primary table
 * (matching VESL commit ceacf10 "Fixing B - MM label").
 */
export function computeDifference(
  primary: ReportData,
  comparison: ReportData,
  tableIndex: number = 0
): TableData {
  // tableIndex: 0=metrics, 1=changes, 2=bridge, 3=growth
  // For changes (1) and growth (3), all values are already % — diff is always pp
  // For bridge (2), values are dollar contributions — diff is currency
  // For metrics (0), use label-based type detection
  const allPct = tableIndex === 1 || tableIndex === 3;
  const tableMap = [
    (r: ReportData) => r.metricsTable,
    (r: ReportData) => r.changesTable,
    (r: ReportData) => r.bridgeTable,
    (r: ReportData) => r.growthTable,
  ];
  const getTable = tableMap[tableIndex] ?? tableMap[0];
  const primaryTable = getTable(primary);
  const comparisonTable = getTable(comparison);
  const rows: TableRow[] = [];

  // Extract scale from row label, e.g. "Revenue USD (MM)" -> 1e6
  const scaleFromLabel = (label: string): number => {
    const m = label.match(/\(([^)]+)\)\s*$/);
    if (!m) return 1;
    switch (m[1].toUpperCase()) {
      case "T":
        return 1e12;
      case "B":
        return 1e9;
      case "MM":
        return 1e6;
      case "M":
        return 1e6;
      case "K":
        return 1e3;
      default:
        return 1;
    }
  };

  // Detect metric type from label for formatting
  const metricType = (label: string): string => {
    if (allPct) return "pct"; // changes and growth tables are all % values
    if (tableIndex === 2) return "currency"; // bridge: all values are dollar contributions
    const l = label.toLowerCase();
    if (l.includes("coverage") || l.includes("ctr")) return "pct";
    if (l.includes("cpc") || l.includes("rpom")) return "unit_currency";
    if (l.includes("revenue")) return "currency";
    if (l.includes("ad count")) return "unit_number";
    return "number";
  };

  // Format a number with thousand separators and 2 decimals.
  const fmt2 = (n: number): string =>
    n.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const rowCount = Math.min(
    primaryTable.rows.length,
    comparisonTable.rows.length
  );

  for (let i = 0; i < rowCount; i++) {
    const pRow = primaryTable.rows[i];
    const cRow = comparisonTable.rows[i];
    const cells: TableCell[] = [];
    // Prefer scale from label suffix; fall back to ReportData.scale
    const scale =
      scaleFromLabel(pRow.label) > 1
        ? scaleFromLabel(pRow.label)
        : primary.scale ?? 1;
    const mType = metricType(pRow.label);

    const cellCount = Math.min(pRow.cells.length, cRow.cells.length);
    for (let j = 0; j < cellCount; j++) {
      const pCell = pRow.cells[j];
      const cCell = cRow.cells[j];

      if (pCell.rawValue == null || cCell.rawValue == null) {
        cells.push({
          value: "-",
          cssClass: "",
          rawValue: null,
        });
      } else {
        const diff = pCell.rawValue - cCell.rawValue;
        const sign = diff > 0 ? "+" : "";
        let formatted: string;

        if (mType === "pct") {
          const pts = diff * 100;
          formatted = `${sign}${fmt2(pts)}%`;
        } else if (mType === "unit_currency") {
          // CPC, RPOM — per-unit dollar values, no scale
          formatted =
            diff < 0 ? `-$${fmt2(Math.abs(diff))}` : `+$${fmt2(diff)}`;
        } else if (mType === "unit_number") {
          // Ad Count — ratio, no scale
          formatted = `${sign}${fmt2(diff)}`;
        } else if (mType === "currency") {
          if (scale > 1) {
            const scaled = diff / scale;
            formatted =
              diff < 0 ? `-$${fmt2(Math.abs(scaled))}` : `+$${fmt2(scaled)}`;
          } else {
            formatted =
              diff < 0 ? `-$${fmt2(Math.abs(diff))}` : `+$${fmt2(diff)}`;
          }
        } else {
          if (scale > 1) {
            const scaled = diff / scale;
            formatted = `${sign}${fmt2(scaled)}`;
          } else {
            formatted = `${sign}${fmt2(diff)}`;
          }
        }

        const cssClass = diff > 0 ? "positive" : diff < 0 ? "negative" : "";
        cells.push({
          value: formatted,
          cssClass,
          rawValue: diff,
        });
      }
    }

    rows.push({ label: pRow.label, cells });
  }

  return { headers: primaryTable.headers, rows };
}
